//@flow
import * as Immutable from 'immutable';

import { MultiDaySlot } from 'common/models/flow/MultiDaySlot';
import type { MultiDaySlotModelType } from 'common/models/flow/MultiDaySlot';

import { TimeSlot } from './TimeSlot';
import TimeUtils from 'common/utils/TimeUtils';

import I18nHelper from 'common/helpers/I18nHelper';

import {
    validatable,
    validate,
    validateAll,
    softValidateAll
} from 'common/utils/ValidationUtil';
import moment from 'moment-timezone';

const translationsNamespace = ['translations', 'validations'];

const nsTranslate = I18nHelper.translate.bind(null, translationsNamespace);

@validatable
class InternalMultiDaySlot extends MultiDaySlot {
    static fromJS(json: MultiDaySlotModelType): InternalMultiDaySlot {
        const state: Object = Object.assign({}, json);
        state.slots = Immutable.List(state.slots).map(item =>
            TimeSlot.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        return false;
    }

    @validateAll
    setAllValidationErrors(): this {
        return this.setSlots(
            this.slots.map(slot => {
                const updatedSlot = slot.setAllValidationErrors();
                return updatedSlot;
            })
        );
    }

    getShortCode() {
        return nsTranslate(`daysShortCode.${this.dayOfWeek}`);
    }

    get slots(): Immutable.List<TimeSlot> {
        return this._state.get('slots');
    }

    @validate
    setSlots(slots: Immutable.List<TimeSlot>): this {
        return super.setSlots(slots);
    }

    sortTimeSlots() {
        const { slots } = this;
        const sortedRanges = slots.sort((previousSlot, currentSlot) => {
            const { startMoment: previousStart } = previousSlot.toMoment();
            const { startMoment: currentStart } = currentSlot.toMoment();
            if (previousStart < currentStart) {
                return -1;
            }
            if (previousStart === currentStart) {
                return 0;
            }
            return 1;
        });
        return sortedRanges;
    }

    isTimeSlotsOverlapping() {
        const sortedSlots = this.sortTimeSlots();
        const isOverlap = sortedSlots.reduce(
            (overlap, currentSlot, index, array) => {
                if (index === 0) {
                    return overlap;
                }
                const previousSlot = array.get(index - 1);
                const { endMoment: previousEnd } = previousSlot.toMoment();
                const { startMoment: currentStart } = currentSlot.toMoment();
                if (previousEnd.isAfter(currentStart)) {
                    return true;
                }
                return overlap;
            },
            false
        );
        return isOverlap;
    }

    validateSlots(): string {
        const { slots } = this;
        const slotWithError = slots.find(slot => slot.hasValidationErrors());
        if (slotWithError) {
            return (
                slotWithError.getValidationMessage('start') ||
                slotWithError.getValidationMessage('end')
            );
        }
        if (this.isTimeSlotsOverlapping()) {
            return nsTranslate('validations:timeSlot.overlappingSlots');
        }
    }

    static getTodayTimeSlots(
        slots: Immutable.List<InternalMultiDaySlot>
    ): ?Immutable.List<TimeSlot> {
        const todayDay = TimeUtils.now();
        return this.getDayTimeSlots(slots, todayDay);
    }

    static getDayTimeSlots(
        slots: Immutable.List<InternalMultiDaySlot>,
        date: moment
    ): ?Immutable.List<TimeSlot> {
        const dayInWords: string = date.format('dddd');
        const dayTimeSlots = slots.find(
            slot => slot.dayOfWeek.toLowerCase() === dayInWords.toLowerCase()
        );
        return dayTimeSlots?.slots;
    }
}

export { InternalMultiDaySlot as MultiDaySlot };
